import Image from 'next/image'
import React from 'react'

import { Button } from '@/components/Button/Button'
import { pxToRem } from '@/shared/styles/themes'
import { Container, styled, Typography } from '@mui/material'

type StarPlacement = {
  top: string
  left: string
  size: number
  opacity: number
  delay: string
}

const STARS: StarPlacement[] = [
  { top: '18%', left: '8%', size: 4, opacity: 0.7, delay: '0s' },
  { top: '34%', left: '22%', size: 2, opacity: 0.5, delay: '1.2s' },
  { top: '12%', left: '32%', size: 3, opacity: 0.8, delay: '0.6s' },
  { top: '62%', left: '14%', size: 3, opacity: 0.6, delay: '2.1s' },
  { top: '48%', left: '4%', size: 2, opacity: 0.4, delay: '1.8s' },
  { top: '22%', left: '48%', size: 2, opacity: 0.5, delay: '0.4s' },
  { top: '72%', left: '40%', size: 4, opacity: 0.8, delay: '2.6s' },
  { top: '14%', left: '58%', size: 3, opacity: 0.6, delay: '1s' },
  { top: '82%', left: '54%', size: 2, opacity: 0.5, delay: '3.2s' },
  { top: '32%', left: '68%', size: 4, opacity: 0.9, delay: '0.8s' },
  { top: '6%', left: '78%', size: 3, opacity: 0.7, delay: '2.4s' },
  { top: '64%', left: '88%', size: 2, opacity: 0.4, delay: '1.4s' },
  { top: '44%', left: '94%', size: 3, opacity: 0.6, delay: '0.2s' },
]

export const StarPluckerOutro: React.FC = () => (
  <StyledSection>
    {STARS.map((star, idx) => (
      <StyledStar
        key={idx}
        style={{
          top: star.top,
          left: star.left,
          width: pxToRem(star.size),
          height: pxToRem(star.size),
          opacity: star.opacity,
          animationDelay: star.delay,
        }}
        aria-hidden="true"
      />
    ))}
    <StyledMoon aria-hidden="true" />
    <StyledInner maxWidth="lg">
      <StyledTextBlock>
        <StyledEyebrow>And Every Story Ends Here</StyledEyebrow>
        <StyledHeadline variant="h2">
          Pixie&apos;s plucking stars — want one for your child&apos;s story?
        </StyledHeadline>
        <StyledBody variant="body1">
          Every storybook we make carries a little of her magic. Pick one, personalise it, and let Pixie write your child a story.
        </StyledBody>
        <StyledCta arrow primary size="large" href="/shop">
          Start Your Story
        </StyledCta>
      </StyledTextBlock>
      <StyledPixieWrap aria-hidden="true">
        <Image
          src="/images/pixie-plucker.png"
          alt=""
          fill
          sizes="(max-width: 600px) 180px, (max-width: 900px) 260px, 360px"
          style={{ objectFit: 'contain' }}
        />
      </StyledPixieWrap>
    </StyledInner>
    <StyledMadeWith>
      Made with love from
      <StyledFlag aria-hidden="true">🇦🇺</StyledFlag>
      Australia
    </StyledMadeWith>
  </StyledSection>
)

const StyledSection = styled('section')(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  background: 'linear-gradient(180deg, #0D1E3A 0%, #1A2E52 55%, #243B6B 100%)',
  color: '#FFFFFF',
  padding: theme.spacing(6, 0),
  minHeight: pxToRem(360),

  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(10, 0),
    minHeight: pxToRem(440),
  },
}))

const StyledStar = styled('span')({
  position: 'absolute',
  borderRadius: '50%',
  backgroundColor: '#FFFFFF',
  boxShadow: '0 0 6px rgba(255,255,255,0.9), 0 0 14px rgba(255,230,160,0.55)',
  animation: 'pixie-outro-twinkle 4s ease-in-out infinite',
  pointerEvents: 'none',

  '@keyframes pixie-outro-twinkle': {
    '0%, 100%': { filter: 'brightness(1)', transform: 'scale(1)' },
    '50%': { filter: 'brightness(2.2)', transform: 'scale(1.4)' },
  },
})

const StyledMoon = styled('span')(({ theme }) => ({
  position: 'absolute',
  top: pxToRem(40),
  right: pxToRem(48),
  width: pxToRem(54),
  height: pxToRem(54),
  borderRadius: '50%',
  background: 'radial-gradient(circle at 35% 35%, #FFF3D0 0%, #F5DE9A 55%, #D9BE6C 100%)',
  boxShadow: '0 0 30px rgba(255, 236, 178, 0.45)',
  pointerEvents: 'none',

  [theme.breakpoints.down('md')]: {
    width: pxToRem(36),
    height: pxToRem(36),
    top: pxToRem(24),
    right: pxToRem(24),
  },
}))

const StyledInner = styled(Container)(({ theme }) => ({
  position: 'relative',
  display: 'grid',
  gridTemplateColumns: '1fr',
  alignItems: 'center',
  gap: theme.spacing(3),

  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: '1.2fr 1fr',
    gap: theme.spacing(6),
  },
}))

const StyledTextBlock = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  order: 2,

  [theme.breakpoints.up('md')]: {
    order: 1,
    gap: theme.spacing(2.5),
  },
}))

const StyledEyebrow = styled('span')(({ theme }) => ({
  display: 'inline-block',
  fontSize: pxToRem(12),
  fontWeight: 700,
  letterSpacing: pxToRem(1.5),
  textTransform: 'uppercase',
  color: '#FFE083',
  padding: theme.spacing(0.5, 1.25),
  borderRadius: pxToRem(999),
  backgroundColor: 'rgba(255, 224, 131, 0.12)',
  border: '1px solid rgba(255, 224, 131, 0.3)',
}))

const StyledHeadline = styled(Typography)(({ theme }) => ({
  color: '#FFFFFF',
  fontWeight: 700,
  lineHeight: 1.2,
  fontSize: pxToRem(26),

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(36),
  },
}))

const StyledBody = styled(Typography)(({ theme }) => ({
  color: 'rgba(255,255,255,0.8)',
  fontSize: pxToRem(15),
  lineHeight: 1.6,
  maxWidth: pxToRem(520),

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(16),
  },
}))

const StyledCta = styled(Button)(({ theme }) => ({
  color: theme.palette.secondary.main,
  fontWeight: 600,
  padding: theme.spacing(2, 4),
  marginTop: theme.spacing(1),
}))

const StyledPixieWrap = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: pxToRem(280),
  aspectRatio: '3 / 4',
  justifySelf: 'center',
  order: 1,
  filter: 'drop-shadow(0 12px 28px rgba(255, 200, 120, 0.35))',
  animation: 'pixie-outro-float 5s ease-in-out infinite',

  '@keyframes pixie-outro-float': {
    '0%, 100%': { transform: 'translateY(0) rotate(-2deg)' },
    '50%': { transform: 'translateY(-10px) rotate(2deg)' },
  },

  [theme.breakpoints.up('md')]: {
    order: 2,
    maxWidth: pxToRem(360),
    justifySelf: 'end',
  },
}))

const StyledMadeWith = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(0.75),
  marginTop: theme.spacing(4),
  fontSize: pxToRem(13),
  fontWeight: 500,
  letterSpacing: pxToRem(0.5),
  color: 'rgba(255, 255, 255, 0.65)',

  [theme.breakpoints.up('md')]: {
    marginTop: theme.spacing(6),
    fontSize: pxToRem(14),
  },
}))

const StyledFlag = styled('span')({
  fontSize: '1.2em',
  lineHeight: 1,
})
